<template lang="pug">
v-dialog(v-model="show" persistent max-width="1000px" :fullscreen="$vuetify.breakpoint.smAndDown")
    v-card.d-flex.flex-column.dialog-card(:class="{'dialog-card--mobile': $vuetify.breakpoint.smAndDown}")
        v-card-title.headline {{ boarderName }}
        v-card-text.flex-grow-1.flex-shrink-1.card-body.pb-0
            v-tabs(v-model="active_tab" show-arrows next-icon="fas fa-arrow-right" prev-icon="fas fa-arrow-left")
                v-tab {{ $t('boarderDetailDialog.detailTabLabel') }}
                v-tab( :disabled="isChangingTabsDisabled") {{ $t('boarderDetailDialog.calendarTabLabel') }}
                v-tab( :disabled="isChangingTabsDisabled") {{ $t('boarderDetailDialog.credentialsTabLabel') }}
                v-tab( :disabled="isChangingTabsDisabled") {{ $t('boarderDetailDialog.ordersTabLabel') }}
                v-tab( :disabled="isChangingTabsDisabled") {{ $t('boarderDetailDialog.creditTabLabel') }}
                v-tab( :disabled="isChangingTabsDisabled") {{ $t('boarderDetailDialog.billingTabLabel') }}
            div.dialog-content
                v-tabs-items(v-model="active_tab")
                    v-tab-item
                      DetailDialog(max-width="1000px" :boarder="boarder" @isSaveInProgress="onAxiosEventIsRunning")
                    v-tab-item
                        v-card(flat tile )
                            v-card-text.px-0.pb-0
                                v-sheet.d-flex.align-center(tile height="45")
                                    v-btn(icon @click="onPrevButtonClick")
                                        v-icon fas fa-chevron-left
                                    v-spacer
                                    v-sheet.d-flex.align-center.flex-column(tile)
                                        v-progress-circular(v-if="isFetchInProgress" indeterminate)
                                        strong(v-else) {{ calendarHeader }}
                                    v-spacer
                                    v-btn(icon @click="onNextButtonClick")
                                        v-icon fas fa-chevron-right
                                v-calendar(
                                    v-model="calendarValue"
                                    ref="calendar" color="primary" show-week
                                :events="eventList" :event-color="getEventColor" :weekdays="[1,2,3,4,5]"
                                )
                                    template(v-slot:event="{event}")
                                        v-icon.mx-1.event-icon(color="white") fas fa-ban
                                        span {{ getEventLabel(event) }}
                    v-tab-item
                        CredentialsTab(max-width="1000px" :personId="this.boarder.person.id" @fetchInProgress="disabledCloseButton" )
                    v-tab-item
                        OrdersTab(max-width="1000px" :boarder="boarder" :business="business" :settingsData="settingsData" :customer="customer")
                    v-tab-item
                        CreditTab(max-width="1000px" :boarder="boarder" @saved="savedCreditData")
                    v-tab-item
                        BoarderInvoiceTab(max-width="1000px" :boarder="boarder" :isActive="active_tab === 5")

        v-divider
        v-card-actions.justify-end
            v-spacer
            v-btn(color="primary" text @click="onCloseButtonClick" :disabled="isCloseButtonDisabled" ) {{ $t('boarderDetailDialog.closeButtonLabel') }}

</template>

<script lang="ts">
import Vue from 'vue'
import type {BoarderCalendarResponseItem, BoarderResponse} from '@/types/service-response'
import moment from 'moment'
import {Component, Prop, Watch} from 'vue-property-decorator'
import {CalendarEvent} from 'vuetify'
import {Action} from 'vuex-class'
import {
  BOARDER_FETCH_CALENDAR,
  FETCH_BUSINESS_DATA,
  FETCH_CUSTOMER_BY_ID,
  FETCH_TENANT_NAME
} from '@/store/action-types'
import {BoarderCalendarPayload} from '@/types/payload'
import {CalendarEventKind} from '@/types/enums'
import {TranslateResult} from 'vue-i18n'
import CredentialsTab from '@/components/boarder-detail-dialog/CredentialsTab.vue'
import OrdersTab from "@/components/boarder-detail-dialog/OrdersTab.vue";
import CreditTab from "@/components/boarder-detail-dialog/CreditTab.vue";
import DetailDialog from "@/components/boarder-detail-dialog/DetailDialog.vue";
import BoarderInvoiceTab from "@/components/boarder-detail-dialog/BoarderInvoiceTab.vue";
import MenuGroupItem from "@/model/menu/MenuGroupItem";
import BusinessModel from "@/model/menu/BusinessModel";
import TenantName from "@/model/tennant/TenantName";
import i18n from "@/i18n";

@Component({
  components: {
    DetailDialog,
    CredentialsTab,
    OrdersTab,
    CreditTab,
    BoarderInvoiceTab
  },
})
export default class BoarderDetailDialog extends Vue {
  @Prop({type: Boolean, default: false}) readonly show!: boolean
  @Prop({type: Object, required: false}) readonly boarder!: BoarderResponse
  @Action(FETCH_TENANT_NAME) fetchTenantName!: { (): Promise<TenantName[]> }

  $refs!: {
    calendar: Vue & { prev(): void, next(): void },
  }

  @Action(BOARDER_FETCH_CALENDAR) fetchCalendar!: ({ (payload: BoarderCalendarPayload): Promise<BoarderCalendarResponseItem[]> })
  @Action(FETCH_BUSINESS_DATA) fetchBusinessData!: { (): Promise<BusinessModel[]> }
  @Action(FETCH_CUSTOMER_BY_ID) fetchCustomerById!: { (payload: any): Promise<any> }

  private calendarValue: string = moment().format('YYYY-MM-DD')
  private eventList: CalendarEvent[] = []
  private isFetchInProgress: boolean = false
  private isCloseButtonDisabled: boolean = false
  private isCreditDataChanged = false
  private isChangingTabsDisabled: boolean = false
  private business: BusinessModel[] | null = null
  private settingsData: any[] = []
  private customer: any | null = null


  private active_tab: number = 1

  get boarderName(): string {
    if (this.boarder) {
      return `${this.boarder.person.firstName} ${this.boarder.person.familyName}`
    }

    return ''
  }

  get calendarHeader(): string {
    return moment(this.calendarValue).locale(i18n.locale).format('MMMM YYYY').toLocaleUpperCase()
  }

  @Watch('calendarValue', {immediate: true})
  onCalendarValueChange(newVal: string): void {
    const start = moment(newVal).startOf('month').toDate()
    const end = moment(newVal).endOf('month').toDate()

    this.fetchData(start, end)
  }

  onAxiosEventIsRunning(): void {
    this.isChangingTabsDisabled = !this.isChangingTabsDisabled
    this.isCloseButtonDisabled = !this.isCloseButtonDisabled
  }

  disabledCloseButton (isFetchInProgress: boolean): void {
    this.isCloseButtonDisabled = isFetchInProgress
  }

  getEventColor(event: CalendarEvent): string {
    switch (event.kind) {
      case CalendarEventKind.ReservationOptOut:
        return '#646464'
      default:
        return ''
    }
  }

  getEventLabel(event: any): TranslateResult {
    return this.$t(`boarderManagementPage.eventKindLabel.${event.kind}`)
  }

  async fetchData(start: Date, end: Date): Promise<void> {
    this.isFetchInProgress = true

    const payload: BoarderCalendarPayload = {
      boarderId: this.boarder.id,
      start,
      end
    }

    const response: BoarderCalendarResponseItem[] = await this.fetchCalendar(payload)

    this.eventList = response.map((e): CalendarEvent => ({
      kind: e.kind,
      start: moment(e.date).format('YYYY-MM-DD'),
      allDay: true
    }))

    this.isFetchInProgress = false
  }

  savedCreditData (): void {
    this.isCreditDataChanged = true
  }

  onCloseButtonClick(): void {
    this.$emit('close', this.isCreditDataChanged)
  }

  onPrevButtonClick(): void {
    this.$refs.calendar.prev()
  }

  onNextButtonClick(): void {
    this.$refs.calendar.next()
  }

  async fetchBusiness(): Promise<void> {
    try {
      this.business = await this.fetchBusinessData()
      //this.fetchCustomer()
    } catch (e) {

    } finally {
    }
  }

  async fetchTenantData(): Promise<void> {

    try {
      this.settingsData = await this.fetchTenantName()
    } catch (e) {

    } finally {
    }
  }

  async fetchCustomer(): Promise<void> {
    let payload: any | null = null
    if(this.boarder) {
      payload = this.boarder.customer.id
    }

    try {
      this.customer = await this.fetchCustomerById(payload)

    } catch (e) {

    } finally {

    }
  }


  mounted() {
    this.calendarValue = moment().format('YYYY-MM-DD')
    this.fetchBusiness()
    this.fetchTenantData()
    this.fetchCustomer()

  }
}

</script>

<style lang="stylus" scoped>
.dialog-card
  max-height 90vh
  min-height 0
  overflow hidden

.dialog-card--mobile
  max-height 100vh
  height 100vh

.card-body
  display flex
  flex-direction column
  min-height 0
  overflow hidden

.dialog-content
  flex 1 1 auto
  min-height 0
  overflow-y auto
  overflow-x hidden
  overscroll-behavior-x none

.day-status
  display flex
  border-radius 50%
  align-items center
  justify-content center
  height 30px
  width 30px
  background-color silver

.day-canceled
  color darkred !important

.event-icon
  font-size .875em !important
</style>
